import { db, auth } from '../services/firebase.js';

export const Home = {
  data() {
    return {
      user: null,
      isAuthenticated: false,
      vendor: "",
      customerName: "",
      customerPhone: "",
      customerEmail: "",
      serviceType: "",
      serviceLocation: "",
      serviceLat: null,
      serviceLng: null,
      comments: "",
      addressSuggestions: [],
      isLoading: false,
      isSubmitting: false,
      searchTimeout: null,
      quotedPrice: "",
      termsAccepted: false,
      liabilityAccepted: false,
      emailConsent: false,
      nameErrorMessage: "",
      phoneErrorMessage: "",
      emailErrorMessage: "",
      addressErrorMessage: "",
      vendorErrorMessage: "",
      serviceTypeErrorMessage: "",
      priceErrorMessage: "",
      termsErrorMessage: "",
      emailConsentErrorMessage: "",
      autocomplete: null,
      dateSubmitted: "",
      timeSubmitted: "",
      submitError: false,
      estimateData: null,
      additionalServices: []
    };
  },
  inject: ["user", "isAuthenticated"],
  created() {
    // Handle incoming estimate data from query params
    if (this.$route.query.estimateData) {
      try {
        this.estimateData = JSON.parse(this.$route.query.estimateData);
        this.populateEstimateData();
      } catch (e) {
        console.error("Error parsing estimate data:", e);
      }
    }
  },
  methods: {
    initAutocomplete() {
      // Initialize Google Places Autocomplete
      this.autocomplete = new google.maps.places.Autocomplete(
        this.$refs.addressInput,
        {
          types: ["address"],
          componentRestrictions: { country: "US" } // Restrict to US addresses
        }
      );

      // Add place_changed event listener
      this.autocomplete.addListener("place_changed", () => {
        const place = this.autocomplete.getPlace();

        if (!place.formatted_address) {
          this.addressErrorMessage =
            "Please select an address from the dropdown";
          return;
        }

        this.serviceLocation = place.formatted_address;
        // Places already resolved the coordinates as part of this lookup, and
        // they were being discarded - which is why the map re-geocoded every job
        // on every render, one billed request at a time. Keeping them here costs
        // nothing and makes that unnecessary.
        if (place.geometry && place.geometry.location) {
          this.serviceLat = place.geometry.location.lat();
          this.serviceLng = place.geometry.location.lng();
        } else {
          this.serviceLat = null;
          this.serviceLng = null;
        }
        this.addressErrorMessage = "";
      });
    },

    formatPrice(price) {
      const cleanPrice =
        typeof price === "string" ? price.trim() : String(price || "").trim();
      const priceNumber = parseFloat(cleanPrice.replace(/[^0-9.]/g, "")) || 0;

      return priceNumber.toLocaleString("en-US", {
        style: "currency",
        currency: "USD"
      });
    },

    formatPhoneNumber(phone) {
      return phone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
    },

    isValidName() {
      this.nameErrorMessage = "";
      if (this.customerName.trim() === "") {
        this.nameErrorMessage = "Name is required";
        return false;
      }
      if (this.customerName.trim().length < 3) {
        this.nameErrorMessage = "Name must be at least 3 characters long";
        return false;
      }
      return true;
    },

    isValidPhone() {
      this.phoneErrorMessage = "";
      if (this.customerPhone.trim() === "") {
        this.phoneErrorMessage = "Invalid phone number format";
        return false;
      }
      this.customerPhone = this.formatPhoneNumber(this.customerPhone);
      return true;
    },

    isValidEmail() {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

      this.emailErrorMessage = "";

      if (!this.customerEmail) {
        this.emailErrorMessage = "Email is required";
        return false;
      }

      if (!emailRegex.test(this.customerEmail)) {
        this.emailErrorMessage = "Invalid email address";
        return false;
      }

      return true;
    },

    isValidAddress() {
      if (!this.serviceLocation.trim()) {
        this.addressErrorMessage = "Address is required";
        return false;
      }
      return true;
    },

    isValidVendor() {
      this.vendorErrorMessage = "";
      if (this.vendor.trim() === "") {
        this.vendorErrorMessage = "Vendor is required";
        return false;
      }
      return true;
    },

    isValidServiceType() {
      this.serviceTypeErrorMessage = "";
      if (this.serviceType.trim() === "") {
        this.serviceTypeErrorMessage = "Service type is required";
        return false;
      }
      return true;
    },

    isValidPrice() {
      this.priceErrorMessage = "";
      if (!this.quotedPrice || this.quotedPrice.trim() === "") {
        this.priceErrorMessage = "Price is required";
        return false;
      }

      const priceRegex = /^\d+(\.\d{2})?$/;
      if (!priceRegex.test(this.quotedPrice)) {
        this.priceErrorMessage = "Invalid price format";
        return false;
      }

      this.quotedPrice = this.formatPrice(this.quotedPrice);
      return true;
    },

    isValidEmailConsent() {
      this.emailConsentErrorMessage = "";
      if (!this.emailConsent) {
        this.emailConsentErrorMessage = "This field is required";
        return false;
      }
      return true;
    },

    isValidTerms() {
      this.termsErrorMessage = "";
      if (!this.termsAccepted) {
        this.termsErrorMessage = "You must accept all terms and conditions";
        return false;
      }
      return true;
    },

    populateEstimateData() {
      if (this.estimateData) {
        // Set service type based on estimate service type
        this.serviceType = this.estimateData.serviceType || "";

        // Set quoted price from estimate
        this.quotedPrice = this.formatPrice(this.estimateData.totalEstimate);

        // Build comments from estimate details
        let estimateDetails = [
          `Square Footage: ${this.estimateData.squareFootage}`,
          `Base Price: ${this.formatPrice(this.estimateData.basePrice)}`,
          `Surface Condition: ${this.getConditionText(
            this.estimateData.condition
          )}`
        ];

        // Add additional services to comments
        if (
          this.estimateData.additionalServices &&
          this.estimateData.additionalServices.length > 0
        ) {
          estimateDetails.push("Additional Services:");
          this.estimateData.additionalServices.forEach((service) => {
            estimateDetails.push(`- ${service}`);
          });
        }

        // Store additional services for reference
        this.additionalServices = this.estimateData.additionalServices || [];

        // Add urgency level if specified
        if (this.estimateData.urgency) {
          estimateDetails.push(
            `Service Timeline: ${this.getUrgencyText(
              this.estimateData.urgency
            )}`
          );
        }

        // Set comments with formatted estimate details
        this.comments = `Estimate Details:\n${estimateDetails.join("\n")}`;
      }
    },

    getConditionText(multiplier) {
      const conditions = {
        1.0: "Light Dirt/Algae",
        1.2: "Moderate Dirt/Staining",
        1.4: "Heavy Mold/Mildew",
        1.6: "Severe Buildup/Staining"
      };
      return conditions[multiplier] || "Standard Condition";
    },

    getUrgencyText(multiplier) {
      const urgency = {
        1.0: "Standard (1-2 weeks)",
        1.2: "Priority (3-5 days)",
        1.5: "Urgent (24-48 hours)"
      };
      return urgency[multiplier] || "Standard Timeline";
    },

    submitForm() {
      const now = new Date();

      // Format date as MM/DD/YYYY
      const dateSubmitted = now.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric"
      });

      // Format time as HH:MM:SS AM/PM
      const timeSubmitted = now.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true
      });

      if (!auth.currentUser) {
        this.submitError = true;
        console.error('Cannot submit a job while signed out.');
        return;
      }

      const newJob = {
        // Never "anonymous": a job with no real owner cannot be read back by
        // anyone under per-user rules, and this route requires auth anyway.
        userId: auth.currentUser.uid,
        vendor: this.vendor.trim(),
        customerName: this.customerName.trim().toUpperCase(),
        customerPhone: this.formatPhoneNumber(this.customerPhone),
        customerEmail: this.customerEmail.trim().toLowerCase(),
        serviceType: this.serviceType.trim(),
        serviceLocation: this.serviceLocation.trim(),
        // Stored so the map never has to geocode. Null when the address was
        // typed rather than picked from the dropdown; the backfill covers those.
        lat: this.serviceLat,
        lng: this.serviceLng,
        comments: this.comments.trim(),
        quotedPrice: this.formatPrice(this.quotedPrice),
        termsAccepted: this.termsAccepted,
        liabilityAccepted: this.liabilityAccepted,
        emailConsent: this.emailConsent,
        dateSubmitted: dateSubmitted,
        timeSubmitted: timeSubmitted,
        estimate: this.estimateData
          ? {
              squareFootage: this.estimateData.squareFootage,
              basePrice: this.estimateData.basePrice,
              condition: this.estimateData.condition,
              additionalServices: this.estimateData.additionalServices,
              urgency: this.estimateData.urgency,
              totalEstimate: this.estimateData.totalEstimate
            }
          : null
      };

      // Add to both jobs collection and user's jobs
      const jobKey = db.ref("jobs").push().key;
      const updates = {};
      updates[`/jobs/${jobKey}`] = newJob;

      // If user is authenticated, also add reference to user's jobs
      if (this.user) {
        updates[`/users/${this.user.uid}/jobs/${jobKey}`] = true;
      }

      return db
        .ref()
        .update(updates)
        .then(() => {
          console.log("Job submitted successfully!");
          this.submitError = false;
          // this.clearForm();
          this.$router.push("/confirm");
        })
        .catch((error) => {
          console.error("Error submitting job:", error);
          this.submitError = true;
        });

      db.ref("jobs")
        .push(newJob)
        .then(() => {
          console.log("Job submitted successfully!");
          this.submitError = false;
          this.vendor = "";
          this.serviceLat = null;
          this.serviceLng = null;
          this.customerName = "";
          this.customerPhone = "";
          this.customerEmail = "";
          this.serviceType = "";
          this.serviceLocation = "";
          this.comments = "";
          this.termsAccepted = "";
          this.liabilityAccepted = "";
          this.emailConsent = "";
          this.quotedPrice = "";
        })
        .catch((error) => {
          console.error("Error submitting job:", error);
          this.submitError == true;
        });
    },

    async handleSubmit() {
      
      if (this.isSubmitting) return;
      
      this.isSubmitting = true;
      
      try {
        // Validate all fields first
        const isValid =
          this.isValidName() &&
          this.isValidPhone() &&
          this.isValidEmail() &&
          this.isValidAddress() &&
          this.isValidVendor() &&
          this.isValidServiceType() &&
          // this.isValidPrice() &&
          this.isValidTerms();

        if (!isValid) {
          this.submitError = true; // Show error if validation fails
          return;
        }

        await this.submitForm();
        this.$router.push("/confirm");
      } catch (error) {
        this.submitError = true; 
        console.error("Submit error:", error);
        this.isSubmitting = false;
      }
    }
  },
  mounted() {
    this.initAutocomplete();
    auth.onAuthStateChanged((user) => {
      this.user = user;
      this.isAuthenticated = !!user;
    });
  },
  template: "#home"
};
